import { uniqueId } from 'lodash'
import React, { useRef, useState } from 'react'

import type { CourseValueType, ICourseOption } from '../CourseKitTypes'
import type { ICourseMenuProps } from '../CourseMenu/CourseMenu'
import CourseMenu from '../CourseMenu/CourseMenu'
import DropdownCourse from '../DropDownCourse'
import type { IDropdownCourseContent } from '../DropDownCourse/DropdownCourse'
import { useDropDownHotKeys } from './hooks'
import ButtonStyled from './styled/ButtonStyled'

interface IDropdownMenuCourseProps<V extends CourseValueType>
  extends Omit<ICourseMenuProps<V>, 'id'>,
    Pick<IDropdownCourseContent, 'placement' | 'overlay' | 'container'> {
  children: React.ReactNode
  ariaLabel?: string
  buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement> & { [key: `data-${string}`]: string }
}

const DropdownMenuCourse = <V extends CourseValueType>({
  options,
  value,
  children,
  onClick,
  placement = 'bottom-end',
  container,
  ariaLabel,
  buttonProps,
  ...menuProps
}: IDropdownMenuCourseProps<V>) => {
  const [isOpen, setIsOpen] = useState(false)
  const [focusedOptionId, setFocusedOptionId] = useState<string | number | undefined>()

  const menuId = `dropdown-menu-${uniqueId()}`
  const ref = useRef<HTMLButtonElement>(null)

  const handleKeyDown = useDropDownHotKeys({
    options,
    active: options.find((option) => option.value === value) || options[0],
    setIsOpen,
    isOpen,
    focusedOptionIndex: options.findIndex((option) => option.id === focusedOptionId),
    target: ref.current,
    focusedOptionId,
    setFocusedOptionId,
    selectOption: onClick,
  })

  const handleSelect = (option: ICourseOption<V>) => {
    setIsOpen(false)
    setFocusedOptionId(undefined)
    onClick?.(option)
  }

  return (
    <DropdownCourse
      container={container}
      onVisibleChange={setIsOpen}
      overlay={
        <CourseMenu<V>
          focusedId={focusedOptionId}
          id={menuId}
          onClick={handleSelect}
          options={options}
          target={ref.current}
          value={value}
          {...menuProps}
        />
      }
      placement={placement}
      targetRef={ref}
      visible={isOpen}
      flip
      shift
    >
      <ButtonStyled
        aria-activedescendant={focusedOptionId?.toString()}
        aria-controls={menuId}
        aria-expanded={isOpen}
        aria-haspopup='listbox'
        aria-label={ariaLabel}
        onKeyDown={handleKeyDown}
        {...buttonProps}
        type='button'
      >
        {children}
      </ButtonStyled>
    </DropdownCourse>
  )
}

export default DropdownMenuCourse
